import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { AppModal } from "global/components/AppModal";
import CustomDropzone from "global/components/CustomDropzone/CustomDropzone";
import notifiers from "global/constants/NotificationConstants";
import {
  openErrorNotification,
  openSuccessNotification,
  shortenFileName,
} from "helpers/methods";
import t from "../SignerAttachments.tokens";
import {
  attachmentErrorMessage,
  deleteSignerAttachment,
  invalidateSignerAttachments,
  uploadSignerAttachment,
} from "../ParticipantScreenService";
import {
  FileIcon,
  FileImageIcon,
  FilePdfIcon,
  PaperclipIcon,
  TrashIcon,
  UploadCloudIcon,
} from "./SignerExperienceIcons";
import {
  attachmentFileKind,
  formatAllowedTypesSentence,
  formatAttachmentSize,
  getAttachmentPolicy,
  SignerAttachmentItem,
  toAttachmentItem,
} from "./AttachmentPolicy";

const { palette: p } = t;

interface AttachmentUploadDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called after every server change so the field can rewrite `field.value` and commit. */
  onConfirm: (items: SignerAttachmentItem[]) => void;
  onPreview: (item: SignerAttachmentItem) => void;
  initialItems: SignerAttachmentItem[];
  usedElsewhere?: number;
  /** Signing request token — this dialog talks to the callback lane, which has no session. */
  requestToken: string;
  /** `PdfField.name` of the attachment field being filled; the server keys uploads by it. */
  fieldName: string;
  fieldLabel?: string;
  required?: boolean;
  uploadFile?: (file: File) => Promise<SignerAttachmentItem>;
  removeFile?: (id: string) => Promise<void>;
}

interface PendingUpload {
  key: string;
  name: string;
  size: number;
}

const rowGlyph = (name: string) => {
  const kind = attachmentFileKind(name);
  if (kind === "pdf") return <FilePdfIcon size={t.row.iconSize} />;
  if (kind === "image") return <FileImageIcon size={t.row.iconSize} />;
  return <FileIcon size={t.row.iconSize} />;
};

const AttachmentUploadDialog = ({
  open,
  onClose,
  onConfirm,
  onPreview,
  initialItems,
  usedElsewhere = 0,
  requestToken,
  fieldName,
  fieldLabel,
  required,
  uploadFile,
  removeFile,
}: AttachmentUploadDialogProps) => {
  const [items, setItems] = useState<SignerAttachmentItem[]>(initialItems);
  const [policy, setPolicy] = useState(getAttachmentPolicy);
  const [busy, setBusy] = useState<boolean>(false);
  const [pending, setPending] = useState<PendingUpload[]>([]);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setItems(initialItems);
      setPolicy(getAttachmentPolicy());
    }
  }, [open, initialItems]);

  // Measured against the signer's total across every attachment field, because that is what the server
  // enforces. Counting only this field's list would offer slots that the upload then rejects with 409.
  const used = items.length + usedElsewhere;
  const remainingSlots = Math.max(policy.maxFiles - used, 0);
  const isFull = remainingSlots === 0;

  /**
   * Files upload as soon as they are chosen rather than on Done.
   *
   * Three reasons: the server owns the type/size/count rules and its message is the one worth showing;
   * deleting needs the id the server assigns; and a signer who closes the tab mid-way keeps what they
   * already attached. Each file goes up on its own so one rejection does not discard the rest.
   */
  const handleAdd = async (added: File[]) => {
    if (busy) return;
    const remaining = remainingSlots;
    if (remaining <= 0) {
      openErrorNotification(notifiers.attachmentLimitReached(policy.maxFiles));
      return;
    }
    const accepted = added.slice(0, remaining);
    if (added.length > accepted.length) {
      openErrorNotification(
        notifiers.attachmentLimitTrimmed(
          remaining,
          added.length - accepted.length,
        ),
      );
    }
    if (!accepted.length) return;

    const queued: PendingUpload[] = accepted.map((file, index) => ({
      key: `${Date.now()}-${index}-${file.name}`,
      name: file.name,
      size: file.size,
    }));

    setBusy(true);
    setPending(queued);

    let current = items;
    const saved: SignerAttachmentItem[] = [];
    try {
      for (let index = 0; index < accepted.length; index += 1) {
        const file = accepted[index];
        try {
          const item = uploadFile
            ? await uploadFile(file)
            : toAttachmentItem(
                await uploadSignerAttachment(requestToken, fieldName, file),
                file,
              );
          saved.push(item);
          current = [...current, item];
          setItems(current);
        } catch (error: any) {
          openErrorNotification(
            attachmentErrorMessage(error, notifiers.ATTACHMENT_UPLOAD_FAILED),
          );
        } finally {
          setPending((rest) =>
            rest.filter((entry) => entry.key !== queued[index].key),
          );
        }
      }
    } finally {
      setPending([]);
      setBusy(false);
    }

    if (!saved.length) return;
    // The shared list is now stale — drop it so a field mounting later reads the new set. Nothing to
    // invalidate when the files never left the browser.
    if (!uploadFile) invalidateSignerAttachments();
    onConfirm(current);
    openSuccessNotification(
      saved.length === 1
        ? notifiers.ATTACHMENT_ADDED
        : notifiers.attachmentsAdded(saved.length),
    );
  };

  const handleRemove = async (id: string) => {
    if (busy || deletingId) return;
    setBusy(true);
    setDeletingId(id);
    try {
      if (removeFile) {
        await removeFile(id);
      } else {
        await deleteSignerAttachment(requestToken, id);
        invalidateSignerAttachments();
      }
      const next = items.filter((item) => item.id !== id);
      setItems(next);
      onConfirm(next);
      openSuccessNotification(notifiers.ATTACHMENT_DELETED);
    } catch (error: any) {
      openErrorNotification(
        attachmentErrorMessage(error, notifiers.ATTACHMENT_DELETE_FAILED),
      );
    } finally {
      setDeletingId(null);
      setBusy(false);
    }
  };

  // Everything is already saved by the time Done is pressed; this just closes the dialog.
  const handleDone = () => {
    onConfirm(items);
    onClose();
  };

  const dropTitle = (isDragOver: boolean) => {
    if (pending.length)
      return pending.length === 1
        ? "Uploading 1 file…"
        : `Uploading ${pending.length} files…`;
    if (deletingId) return "Removing file…";
    if (isFull) return "Maximum files reached";
    if (isDragOver) return "Drop files to attach";
    return items.length ? "Add another file" : "Click to upload or drop files here";
  };

  // Shows what is left rather than the ceiling once anything has been attached anywhere, so a signer
  // with two files on another field sees "3 more allowed" instead of a misleading "max 5 files".
  const formatsText = `${formatAllowedTypesSentence(policy.allowedTypes)} · up to ${
    policy.maxFileSizeMb
  } MB each · ${used ? `${remainingSlots} more allowed` : `max ${policy.maxFiles} files`}`;

  const iconButtonSx = (danger: boolean) => ({
    width: t.row.actionButtonSize,
    height: t.row.actionButtonSize,
    flexShrink: 0,
    borderRadius: t.row.actionRadius,
    border: `1px solid ${danger ? p.dangerBorder : p.border}`,
    backgroundColor: p.white,
    color: danger ? p.danger : p.meta,
    "&:hover": { backgroundColor: danger ? t.row.dangerHover : p.hover },
    "&.Mui-focusVisible": {
      outline: `2px solid ${p.accent}`,
      outlineOffset: "2px",
    },
  });

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title={fieldLabel || "Add attachment"}
      subtitle={
        items.length
          ? "Attach one or more files. You can open or delete any of them."
          : "Attach one or more files to complete this field."
      }
      icon={<PaperclipIcon size={18} />}
      headerAction={
        required ? (
          <Box
            component="span"
            sx={{
              fontSize: t.requiredPill.fontSize,
              fontWeight: t.requiredPill.fontWeight,
              color: t.requiredPill.color,
              backgroundColor: t.requiredPill.background,
              borderRadius: t.requiredPill.radius,
              px: t.requiredPill.paddingX,
              py: t.requiredPill.paddingY,
              whiteSpace: "nowrap",
            }}
          >
            Required
          </Box>
        ) : undefined
      }
      width={t.dialog.width}
      primaryLabel="Done"
      onPrimary={handleDone}
      busy={busy}
      primaryDisabled={busy || (required && items.length === 0)}
      cancelLabel="Cancel"
      onCancel={onClose}
      footerStart={
        <Typography
          sx={{ fontSize: t.footer.countFontSize, color: p.meta }}
          role="status"
        >
          {pending.length
            ? `Uploading ${pending.length} ${
                pending.length === 1 ? "file" : "files"
              }…`
            : deletingId
              ? "Removing file…"
              : used
                ? // The total is the signer's across every attachment field, so name where the rest
                  // are — otherwise "3 of 5 attached" reads as wrong on a field showing one file.
                  `${used} of ${policy.maxFiles} attached${
                    usedElsewhere
                      ? ` · ${usedElsewhere} on other field${usedElsewhere === 1 ? "" : "s"}`
                      : ""
                  }`
                : "No files yet"}
        </Typography>
      }
    >
      <CustomDropzone
        onAdd={handleAdd}
        maxFileSize={policy.maxFileSizeMb * 1024 * 1024}
        acceptedFiles={policy.acceptedFiles}
        multipleFileUpload={true}
        showAlerts={true}
        // Same duplicate guard the sign-request upload uses: CustomDropzone rejects a file whose name is
        // already in this list, with "File [x] already exists. Please try a different file." Reusing the
        // prop keeps the message and the behaviour identical to the rest of the app.
        //
        // Names here are the SANITISED ones the server stored, so a re-upload of "my file.pdf" (which is
        // saved as "my_file.pdf") slips past. That matches the sign-request flow, which is also
        // client-side only; the server still accepts it and both copies remain valid.
        fileNames={items.map((item) => item.name)}
        disabled={isFull || busy}
        disabledMessage={notifiers.attachmentLimitReached(policy.maxFiles)}
        uniqueId="signer-attachment"
        ariaLabel="Upload attachment"
        sx={{
          border: `${t.dropzone.borderWidth} dashed ${t.dropzone.restBorder}`,
          borderColor: t.dropzone.restBorder,
          borderRadius: t.dropzone.radius,
          backgroundColor: t.dropzone.restBackground,
          textAlign: "left",
          cursor: isFull ? "not-allowed" : "pointer",
          opacity: isFull ? t.dropzone.disabledOpacity : 1,
          transition: "border-color 0.2s, background-color 0.2s",
        }}
        dragOverSx={{
          borderColor: t.dropzone.activeBorder,
          backgroundColor: t.dropzone.activeBackground,
        }}
        innerBody={({ isDragOver }) => (
          <Stack
            direction="row"
            alignItems="center"
            sx={{ gap: t.dropzone.gap, padding: t.dropzone.padding }}
          >
            <Box sx={{ display: "flex", flexShrink: 0, color: p.accent }}>
              <UploadCloudIcon size={t.dropzone.iconSize} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: t.dropzone.titleFontSize,
                  fontWeight: t.dropzone.titleFontWeight,
                  color: p.ink,
                }}
              >
                {dropTitle(isDragOver)}
              </Typography>
              <Typography
                sx={{
                  marginTop: t.dropzone.metaGap,
                  fontSize: t.dropzone.metaFontSize,
                  color: p.meta,
                }}
              >
                {formatsText}
              </Typography>
            </Box>
          </Stack>
        )}
      />

      {isFull && (
        <Typography
          role="status"
          sx={{
            marginTop: t.notice.marginTop,
            fontSize: t.notice.fontSize,
            color: p.danger,
          }}
        >
          {/* The limit is shared, so a field can be full with nothing of its own to delete —
              telling that signer to "delete one" here would point at an empty list. */}
          Maximum of {policy.maxFiles} files reached.{" "}
          {items.length
            ? "Delete one to add another."
            : "Remove a file from another attachment field to add one here."}
        </Typography>
      )}

      {items.length === 0 && pending.length === 0 ? (
        <Box
          sx={{
            marginTop: t.empty.marginTop,
            border: t.empty.border,
            borderRadius: t.empty.radius,
            padding: t.empty.padding,
            textAlign: "center",
            fontSize: t.empty.fontSize,
            lineHeight: t.empty.lineHeight,
            color: p.muted,
          }}
        >
          {required
            ? "No files attached yet. Add at least one to complete this field."
            : "No files attached yet."}
        </Box>
      ) : (
        <Stack
          component="ul"
          spacing={t.row.stackGap}
          sx={{
            listStyle: "none",
            p: 0,
            m: 0,
            marginTop: t.row.stackGap,
          }}
        >
          {items.map((item) => (
            <Stack
              key={item.id}
              component="li"
              direction="row"
              alignItems="center"
              sx={{
                gap: t.row.gap,
                border: t.row.border,
                borderRadius: t.row.radius,
                backgroundColor: t.row.background,
                paddingX: t.row.paddingX,
                paddingY: t.row.paddingY,
              }}
            >
              <Box
                sx={{
                  width: t.row.iconTileSize,
                  height: t.row.iconTileSize,
                  flexShrink: 0,
                  borderRadius: t.row.iconTileRadius,
                  backgroundColor: t.row.iconTileBackground,
                  color: p.accent,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {rowGlyph(item.name)}
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Middle-trimmed so the extension stays readable, with the full name on hover — the
                    same Tooltip treatment DocumentsCard uses for truncated document names. The CSS
                    ellipsis stays as the backstop for a narrow viewport. */}
                <Tooltip title={item.name} arrow describeChild>
                  <Typography
                    sx={{
                      fontSize: t.row.nameFontSize,
                      fontWeight: t.row.nameFontWeight,
                      color: p.ink,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {shortenFileName(item.name)}
                  </Typography>
                </Tooltip>
                <Typography
                  sx={{
                    marginTop: t.row.metaGap,
                    fontSize: t.row.metaFontSize,
                    color: p.meta,
                  }}
                >
                  {formatAttachmentSize(item.size)} · added {item.addedAt}
                </Typography>
              </Box>

              <Stack
                direction="row"
                alignItems="center"
                sx={{ gap: t.row.actionGap }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  disabled={deletingId === item.id}
                  onClick={() => onPreview(item)}
                  aria-label={`Open ${item.name}`}
                  sx={{
                    height: t.row.actionHeight,
                    px: t.row.actionPaddingX,
                    borderRadius: t.row.actionRadius,
                    fontSize: t.row.actionFontSize,
                    fontWeight: t.row.actionFontWeight,
                    color: p.body,
                    borderColor: p.border,
                    whiteSpace: "nowrap",
                  }}
                >
                  Open
                </Button>
                {/* No rename control: the server sanitises the file name once at upload and derives
                    the S3 key from it, so the stored name is immutable. A local-only rename would
                    show the signer a name the sender never receives. */}
                <IconButton
                  aria-label={
                    deletingId === item.id
                      ? `Removing ${item.name}`
                      : `Delete ${item.name}`
                  }
                  title="Delete"
                  disabled={busy}
                  onClick={() => handleRemove(item.id)}
                  sx={iconButtonSx(true)}
                >
                  {deletingId === item.id ? (
                    <CircularProgress
                      size={t.row.actionSpinnerSize}
                      color="inherit"
                    />
                  ) : (
                    <TrashIcon size={14} />
                  )}
                </IconButton>
              </Stack>
            </Stack>
          ))}

          {pending.map((entry) => (
            <Stack
              key={entry.key}
              component="li"
              direction="row"
              alignItems="center"
              sx={{
                gap: t.row.gap,
                border: t.row.border,
                borderRadius: t.row.radius,
                backgroundColor: t.row.background,
                paddingX: t.row.paddingX,
                paddingY: t.row.paddingY,
                opacity: t.row.pendingOpacity,
              }}
            >
              <Box
                sx={{
                  width: t.row.iconTileSize,
                  height: t.row.iconTileSize,
                  flexShrink: 0,
                  borderRadius: t.row.iconTileRadius,
                  backgroundColor: t.row.iconTileBackground,
                  color: p.accent,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {rowGlyph(entry.name)}
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: t.row.nameFontSize,
                    fontWeight: t.row.nameFontWeight,
                    color: p.ink,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {entry.name}
                </Typography>
                <Typography
                  sx={{
                    marginTop: t.row.metaGap,
                    fontSize: t.row.metaFontSize,
                    color: p.meta,
                  }}
                >
                  {formatAttachmentSize(entry.size)} · Uploading…
                </Typography>
              </Box>

              <Box
                sx={{
                  width: t.row.actionButtonSize,
                  height: t.row.actionButtonSize,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: p.accent,
                }}
              >
                <CircularProgress
                  size={t.row.spinnerSize}
                  color="inherit"
                  aria-label={`Uploading ${entry.name}`}
                />
              </Box>
            </Stack>
          ))}
        </Stack>
      )}
    </AppModal>
  );
};

export default AttachmentUploadDialog;
